import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { QualityAssessment } from '@/components/CameraView';

/** Quality feedback threshold - below this shows warning */
export const LOW_QUALITY_THRESHOLD = 0.6;

interface QualityDotProps {
  /** Quality assessment result (null when not available) */
  quality: QualityAssessment | null;
  /** Whether to show the indicator */
  show: boolean;
  /** Show as live preview (different label wording) */
  isLive?: boolean;
}

/**
 * Compact quality indicator — a small coloured dot for the CameraTopBar.
 * Tap to expand and see details (message + score). Tap again to collapse.
 */
export function QualityDot({ quality, show, isLive = false }: QualityDotProps) {
  const [expanded, setExpanded] = useState(false);

  if (!show || quality === null) return null;

  const { isAcceptable, overallScore, rejectionReasons, warnings } = quality;

  const isBlocked = !isAcceptable;
  const isWarning = isAcceptable && (overallScore < LOW_QUALITY_THRESHOLD || warnings.length > 0);
  const isGood = isAcceptable && overallScore >= LOW_QUALITY_THRESHOLD && warnings.length === 0;

  const getMessage = () => {
    if (isBlocked && rejectionReasons.length > 0) return rejectionReasons[0];
    if (warnings.length > 0) return warnings[0];
    if (isGood) return isLive ? 'Ready' : 'Good quality';
    return 'Low quality';
  };

  const dotColor = cn(
    'rounded-full transition-colors',
    isBlocked && 'bg-red-500',
    isWarning && 'bg-yellow-500',
    isGood && 'bg-green-500'
  );

  return (
    <button
      onClick={() => setExpanded(!expanded)}
      className="p-2 rounded-md bg-black/40 hover:bg-black/60 transition-colors flex items-center gap-2"
      role="status"
      aria-live="polite"
      aria-label={`Quality: ${getMessage()} ${(overallScore * 100).toFixed(0)}%`}
      data-testid="quality-dot"
    >
      <span className={cn(dotColor, 'h-3 w-3 shrink-0')} />
      {expanded && (
        <span className="text-white text-xs font-medium whitespace-nowrap">
          {getMessage()} ({(overallScore * 100).toFixed(0)}%)
        </span>
      )}
    </button>
  );
}
